import { useCallback, useEffect, useRef, useState } from "react";
import { getVoiceToken, startHumanCall } from "../services/callsService";
import type { Lead } from "../services/leadsService";

// ============================================================================
// useSoftphone — browser softphone for HUMAN lead calls over Twilio Voice.
//
// Boots a Twilio.Device with an access token from the backend; placing a call
// first opens a crm_calls record (startHumanCall → callId) and then connects
// the WebRTC leg, passing the callId so the TwiML webhook can dial the lead and
// record the call. The recording is transcribed + qualified server-side, so the
// frontend only drives the live call (connect / mute / hangup / timer).
//
// The Twilio Voice SDK is imported dynamically to keep it out of the main bundle
// and only loaded when an agent actually places a call.
// ============================================================================

export type SoftphoneStatus =
  | "idle"
  | "connecting" // creating the record + dialing
  | "ringing"
  | "active"
  | "ended"
  | "error";

export interface SoftphoneState {
  status: SoftphoneStatus;
  lead: Lead | null;
  callId: string | null;
  muted: boolean;
  /** epoch ms when the call was answered (for the duration timer), or null. */
  startedAt: number | null;
  error: string | null;
}

const INITIAL: SoftphoneState = {
  status: "idle",
  lead: null,
  callId: null,
  muted: false,
  startedAt: null,
  error: null,
};

export function useSoftphone() {
  const [state, setState] = useState<SoftphoneState>(INITIAL);

  // Twilio SDK handles (typed loosely to avoid a hard build dependency on the
  // SDK's exported types here; the runtime objects come from the dynamic import).
  const deviceRef = useRef<any>(null);
  const callRef = useRef<any>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      try {
        callRef.current?.disconnect?.();
        deviceRef.current?.destroy?.();
      } catch {
        /* noop */
      }
    };
  }, []);

  const patch = useCallback((p: Partial<SoftphoneState>) => {
    if (mountedRef.current) setState((s) => ({ ...s, ...p }));
  }, []);

  /** Lazily build (or reuse) the Twilio Device, registering it with a fresh token. */
  const ensureDevice = useCallback(async () => {
    if (deviceRef.current) return deviceRef.current;

    const { token } = await getVoiceToken();
    const { Device } = await import("@twilio/voice-sdk");
    const device = new Device(token, {
      codecPreferences: ["opus", "pcmu"] as any,
      closeProtection: true,
    });

    device.on("error", (err: any) => {
      console.error("[softphone] device error:", err?.message || err);
      patch({ status: "error", error: err?.message || "Error del dispositivo de voz" });
    });
    // Refresh the access token before it expires so long sessions keep working.
    device.on("tokenWillExpire", async () => {
      try {
        const { token: fresh } = await getVoiceToken();
        device.updateToken(fresh);
      } catch (e) {
        console.error("[softphone] token refresh failed:", e);
      }
    });

    await device.register();
    deviceRef.current = device;
    return device;
  }, [patch]);

  /** Place a call to a lead. Opens the call record, then connects the WebRTC leg. */
  const startCall = useCallback(
    async (lead: Lead, crmAgentId?: string) => {
      if (state.status === "connecting" || state.status === "active" || state.status === "ringing") {
        return; // already on a call
      }
      patch({ status: "connecting", lead, callId: null, error: null, muted: false, startedAt: null });
      try {
        const device = await ensureDevice();
        const { callId } = await startHumanCall(lead.id, crmAgentId);
        patch({ callId });

        const call = await device.connect({ params: { callId } });
        callRef.current = call;
        patch({ status: "ringing" });

        call.on("accept", () => patch({ status: "active", startedAt: Date.now() }));
        call.on("disconnect", () => {
          callRef.current = null;
          patch({ status: "ended" });
        });
        call.on("cancel", () => {
          callRef.current = null;
          patch({ status: "ended" });
        });
        call.on("reject", () => {
          callRef.current = null;
          patch({ status: "ended" });
        });
        call.on("error", (err: any) => {
          console.error("[softphone] call error:", err?.message || err);
          callRef.current = null;
          patch({ status: "error", error: err?.message || "La llamada falló" });
        });
      } catch (e) {
        const err = e as { response?: { data?: { message?: string } }; message?: string };
        const message = err.response?.data?.message || err.message || "No se pudo iniciar la llamada";
        console.error("[softphone] startCall failed:", message);
        patch({ status: "error", error: message });
      }
    },
    [state.status, ensureDevice, patch],
  );

  const hangup = useCallback(() => {
    try {
      callRef.current?.disconnect?.();
    } catch {
      /* noop */
    }
    callRef.current = null;
    patch({ status: "ended" });
  }, [patch]);

  const toggleMute = useCallback(() => {
    const call = callRef.current;
    if (!call) return;
    const next = !state.muted;
    try {
      call.mute(next);
      patch({ muted: next });
    } catch {
      /* noop */
    }
  }, [state.muted, patch]);

  /** Send a DTMF digit (dial pad), e.g. for IVR menus. */
  const sendDigit = useCallback((digit: string) => {
    try {
      callRef.current?.sendDigits?.(digit);
    } catch {
      /* noop */
    }
  }, []);

  /** Clear an ended/error call so the panel can close. */
  const reset = useCallback(() => {
    callRef.current = null;
    patch({ ...INITIAL });
  }, [patch]);

  return { state, startCall, hangup, toggleMute, sendDigit, reset };
}
